import React, { FC, useState } from "react";
import ButtonComponent from "../../buttons/ButtonComponent";

/** Ответ сервера по паролю точки (`GET` и `rotate`). */
export interface IApPasswordInfo {
  apPassword?: string | null;
  state: string;
  changedAt: string | null;
}

interface PopupModuleApPasswordProps {
  /** Видит и меняет пароль только root — то же правило, что на сервере. */
  isRoot: boolean;
  /** `GET /module/:id/ap-password` — только по нажатию, не при загрузке карточки. */
  onFetch: () => Promise<IApPasswordInfo>;
  /** `POST /module/:id/ap-password/rotate` — новый пароль уходит изделию. */
  onRotate: () => Promise<IApPasswordInfo>;
  /** `POST /module/:id/ap-password/import` — внесение из паспорта (Б4). */
  onImport: (apPassword: string) => Promise<IApPasswordInfo>;
}

// `Б2.4`: подписи пяти состояний — дословно из критерия, не «по смыслу».
const STATE_LABELS: Record<string, string> = {
  none: "не выпущен",
  issued: "выпущен, изделию не передан",
  pending: "ожидает изделие",
  confirmed: "подтверждён",
  unsupported: "прошивка не поддерживает",
};

// `Б2.4`: на 503 текст сервера не показываем — он технический.
const KEY_NOT_CONFIGURED =
  "Ключ пароля на сервере не задан — сообщите руководителю проекта";

function stateLabel(state: string | null): string {
  if (state === null) return "состояние не запрашивалось";
  return STATE_LABELS[state] || `состояние: ${state}`;
}

function errorMessage(error: any): string {
  if (error?.response?.status === 503) return KEY_NOT_CONFIGURED;

  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    "не удалось выполнить действие"
  );
}

/**
 * `Б2.2` (`oq-ММБ-185`): пароль точки доступа МДБН в карточке модуля.
 *
 * Почему карточка не читает пароль при загрузке: чтение значения пишется в
 * журнал действий (`Б2` п. 3) и открывает человеку секрет. Поэтому состояние
 * до первого действия — «не запрашивалось», а значение появляется только после
 * нажатия «показать»; после успешной смены значение скрывается.
 *
 * Смена — в два шага: первый клик показывает подтверждение, второй отправляет
 * команду изделию. Ошибку изделия (`unsupported`) и ожидание (`pending`) карточка
 * показывает по ответу сервера, а не по своим предположениям.
 */
export const PopupModuleApPassword: FC<PopupModuleApPasswordProps> = ({
  isRoot,
  onFetch,
  onRotate,
  onImport,
}) => {
  const [state, setState] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [importValue, setImportValue] = useState("");
  const [copied, setCopied] = useState(false);

  if (!isRoot) return null;

  const run = async (action: () => Promise<IApPasswordInfo>) => {
    setError(null);
    setBusy(true);

    try {
      const result = await action();
      setState(result.state);
      return result;
    } catch (failure: any) {
      setError(errorMessage(failure));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const showPassword = async () => {
    const result = await run(onFetch);

    if (result) {
      setPassword(result.apPassword ?? null);
      setCopied(false);
    }
  };

  const hidePassword = () => {
    setPassword(null);
    setCopied(false);
  };

  const copyPassword = async () => {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      setError("не удалось скопировать — выделите пароль вручную");
    }
  };

  const rotate = async () => {
    setConfirmingRotate(false);
    hidePassword();

    const result = await run(onRotate);

    if (result && result.state === "unsupported") {
      setError("изделие не поддерживает смену пароля — действующий остался прежним");
    }
  };

  const importPassword = async () => {
    const result = await run(() => onImport(importValue));

    if (result) setImportValue("");
  };

  const canImport = state === null || state === "none";

  return (
    <div className="popup_module_ap_password">
      <div className="popup_module_ap_password_head">ПАРОЛЬ ТОЧКИ ДОСТУПА</div>

      <div className="popup_module_ap_password_state">{stateLabel(state)}</div>

      {password ? (
        <div className="popup_module_ap_password_row">
          <code className="popup_module_ap_password_code">{password}</code>
          <ButtonComponent onClick={hidePassword} title="скрыть" />
        </div>
      ) : null}

      <div className="popup_module_ap_password_actions">
        {password ? (
          <ButtonComponent
            onClick={copyPassword}
            disabled={busy}
            title={copied ? "скопировано" : "скопировать"}
          />
        ) : (
          <ButtonComponent
            onClick={showPassword}
            disabled={busy}
            title="показать пароль точки"
          />
        )}

        {confirmingRotate ? (
          <>
            <ButtonComponent
              onClick={rotate}
              disabled={busy}
              title="подтвердить смену"
            />
            <ButtonComponent
              onClick={() => setConfirmingRotate(false)}
              disabled={busy}
              title="отмена"
            />
          </>
        ) : (
          <ButtonComponent
            onClick={() => setConfirmingRotate(true)}
            disabled={busy}
            title="сменить пароль"
          />
        )}
      </div>

      {confirmingRotate ? (
        <div className="popup_module_ap_password_hint">
          Новый пароль уйдёт изделию; действующий останется рабочим, пока изделие
          не подтвердит смену.
        </div>
      ) : null}

      {canImport ? (
        <div className="popup_module_ap_password_import">
          <div className="popup_module_ap_password_hint">
            Для изделия, заведённого до появления пароля на сервере: внесите
            пароль из паспорта. Изделию команда не отправляется.
          </div>
          <div className="popup_module_ap_password_import_row">
            <input
              type="password"
              autoComplete="new-password"
              placeholder="пароль из паспорта"
              value={importValue}
              disabled={busy}
              onChange={(event) => setImportValue(event.target.value)}
            />
            <ButtonComponent
              onClick={importPassword}
              disabled={busy || importValue.length === 0}
              title="внести из паспорта"
            />
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="popup_module_ap_password_error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
};
